import { permissionsByEntity, useCheckPermissions } from '@cmg/auth';
import { routeUtil } from '@cmg/common';
import { PageLayout } from '@cmg/design-system';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import routeFactory from '../../../common/util/routeFactory';
import type { UUID } from '../../../types/common';
import { fetchCrmIntegration } from '../shared/ducks';
import CRMFilesUploadRoute from './crm-files-upload/CRMFilesUploadRoute';
import { CRMInvestorFirmsRoute } from './crm-investor-firms/CRMInvestorFirmsRoute';
import { CMRInvestorFirmCreateRoute } from './crm-investor-firms/investor-firm-detail/CRMInvestorFirmCreateRoute';
import { CMRInvestorFirmDetailRoute } from './crm-investor-firms/investor-firm-detail/CRMInvestorFirmDetailRoute';
import { CRMSalesCoverageRoute } from './crm-sales-coverage/CRMSalesCoverageRoute';
import CRMSalesCoverageEmployeeCreateRoute from './crm-sales-coverage/employee-detail/CRMSalesCoverageEmployeeCreateRoute';
import CRMSalesCoverageEmployeeDetailRoute from './crm-sales-coverage/employee-detail/CRMSalesCoverageEmployeeDetailRoute';
import { CRMManagementDomainObject } from './CRMManagementDomainObject';

export type RouteParams = { crmIntegrationId?: UUID };

// Routes below are mounted at either `${routeFactory.customerCrmManagement.routePath}/*`
// (customer facing) or `${routeFactory.sysAdminCrmManagement.routePath}/*` (sys admin facing),
// so v6 Route paths here must be relative to the corresponding base path.
const relCustomer = (routePath: string) =>
  routeUtil.getRelativeRoutePath(routeFactory.customerCrmManagement.routePath, routePath);
const relSysAdmin = (routePath: string) =>
  routeUtil.getRelativeRoutePath(routeFactory.sysAdminCrmManagement.routePath, routePath);

/**
 * Entry point for accessing CRM Management. All routes associated with viewing/managing crm data stem from here.
 */
export const CRMManagementRoute: React.FC = () => {
  const dispatch = useDispatch();

  const { crmIntegrationId } = useParams<RouteParams>();
  const hasFullCRMAccess = useCheckPermissions([permissionsByEntity.CRM.FULL]);
  const hasFullGlobalCrmAccess = useCheckPermissions([permissionsByEntity.GlobalCrm.FULL]);

  useEffect(() => {
    function fetchCrmIntegrationAndAccount() {
      if (crmIntegrationId) {
        dispatch(fetchCrmIntegration({ crmIntegrationId }));
      }
    }

    fetchCrmIntegrationAndAccount();
  }, [dispatch, crmIntegrationId]);

  return (
    <PageLayout header={<CRMManagementDomainObject />} flex={1}>
      {hasFullCRMAccess && (
        <Routes>
          <Route
            path={relCustomer(routeFactory.customerCrmInvestorFirms.routePath)}
            element={<CRMInvestorFirmsRoute />}
          />
          <Route
            path={relCustomer(routeFactory.customerCrmInvestorFirmsNew.routePath)}
            element={<CMRInvestorFirmCreateRoute />}
          />
          <Route
            path={`${relCustomer(routeFactory.customerCrmInvestorFirm.routePath)}/*`}
            element={<CMRInvestorFirmDetailRoute />}
          />
          <Route
            path={relCustomer(routeFactory.customerCrmSalesCoverage.routePath)}
            element={<CRMSalesCoverageRoute />}
          />
          <Route
            path={relCustomer(routeFactory.customerCrmSalesCoverageEmployeeNew.routePath)}
            element={<CRMSalesCoverageEmployeeCreateRoute />}
          />
          <Route
            path={`${relCustomer(routeFactory.customerCrmSalesCoverageEmployee.routePath)}/*`}
            element={<CRMSalesCoverageEmployeeDetailRoute />}
          />
          <Route
            path={relCustomer(routeFactory.customerCrmFilesUpload.routePath)}
            element={<CRMFilesUploadRoute />}
          />
          <Route
            path="*"
            element={<Navigate to={routeFactory.customerCrmInvestorFirms.routePath} replace />}
          />
        </Routes>
      )}
      {hasFullGlobalCrmAccess && (
        <Routes>
          <Route
            path={relSysAdmin(routeFactory.sysAdminCrmInvestorFirms.routePath)}
            element={<CRMInvestorFirmsRoute />}
          />
          <Route
            path={relSysAdmin(routeFactory.sysAdminCrmInvestorFirmsNew.routePath)}
            element={<CMRInvestorFirmCreateRoute />}
          />
          <Route
            path={`${relSysAdmin(routeFactory.sysAdminCrmInvestorFirm.routePath)}/*`}
            element={<CMRInvestorFirmDetailRoute />}
          />
          <Route
            path={relSysAdmin(routeFactory.sysAdminCrmSalesCoverage.routePath)}
            element={<CRMSalesCoverageRoute />}
          />
          <Route
            path={relSysAdmin(routeFactory.sysAdminCrmSalesCoverageEmployeeNew.routePath)}
            element={<CRMSalesCoverageEmployeeCreateRoute />}
          />
          <Route
            path={`${relSysAdmin(routeFactory.sysAdminCrmSalesCoverageEmployee.routePath)}/*`}
            element={<CRMSalesCoverageEmployeeDetailRoute />}
          />
          <Route
            path={relSysAdmin(routeFactory.sysAdminCrmFilesUpload.routePath)}
            element={<CRMFilesUploadRoute />}
          />
          <Route
            path="*"
            element={
              <Navigate
                to={routeFactory.sysAdminCrmInvestorFirms.getUrlPath({ crmIntegrationId })}
                replace
              />
            }
          />
        </Routes>
      )}
    </PageLayout>
  );
};
